import React from "react";
import styled from "styled-components";
import { Dialog, IconButton } from "@mui/material";
import { ExpressionBuilder } from "../../../../Expressions/ExpressionBuilder";
import { RuleTableBuilder } from "../../../../Rule/Table/RuleTable/RuleTableBuilder";
import { ValueTableRuleBuilder } from "../../../../Rule/Table/ValueTable/ValueTableRuleBuilder";
import { TextMergeBuilder } from "../../../../TextMerge/TextMergeBuilder";
import { CloseGrayIcon } from "../../../../CommonUI/Icon/CloseGrayIcon";
import { ExpandIcon } from "../../../../CommonUI/Icon/ExpandIcon";
import { StatementRuleConfiguration } from "../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import { RuleTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Rule/RuleTableConfiguration";
import { ValueTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { TextMergeConfiguration } from "../../../../rule-engine-core/TextMerge/TextMergeConfiguration";
import type { DemoRuleLogicDefinition } from "../../rules/ruleLogicDefinitions";
import type { AppGlueStorageBackedConfig, RuleEditorSurface } from "../ruleIntegrationTypes";

export type IntegratedRuleEditorHostKey =
    | "StatementRuleConfiguration"
    | "RuleTableConfiguration"
    | "ValueTableConfiguration"
    | "TextMergeConfiguration";

export const INTEGRATED_RULE_EDITOR_SURFACE_TEST_ID = "electronics-integrated-rule-editor-host";
export const INTEGRATED_RULE_EDITOR_FRAME_TEST_ID = "electronics-integrated-rule-builder-frame";
export const INTEGRATED_RULE_EDITOR_POPOUT_TEST_ID = "electronics-integrated-rule-builder-popout";

type IntegratedRuleEditorHostRenderContext = {
    ruleDefinition: DemoRuleLogicDefinition;
    config: AppGlueStorageBackedConfig;
    onStorageRefresh: () => void;
    onCancel: () => void;
    refreshAfterNativeInteraction: () => void;
    renderLargeEditorContext?: () => React.ReactNode;
};

export type IntegratedRuleEditorHostRegistration = {
    executableType: IntegratedRuleEditorHostKey;
    editorSurface: RuleEditorSurface;
    title: string;
    isWide: boolean;
    isConfig: (config: AppGlueStorageBackedConfig) => boolean;
    description: (ruleDefinition: DemoRuleLogicDefinition) => React.ReactNode;
    renderEditor: (context: IntegratedRuleEditorHostRenderContext) => React.ReactNode;
};

export const INTEGRATED_RULE_EDITOR_HOSTS: Record<IntegratedRuleEditorHostKey, IntegratedRuleEditorHostRegistration> = {
    StatementRuleConfiguration: {
        executableType: "StatementRuleConfiguration",
        editorSurface: "native_appglue_editor",
        title: "Native AppGlue Expression Editor",
        isWide: false,
        isConfig: (config) => config instanceof StatementRuleConfiguration,
        description: (ruleDefinition) => (
            <>
                Editing uses the AppGlue <code>{ruleDefinition.targetAppGlueObjectLabel}</code> surface. Demo-specific
                condition, required-field, and action textareas are intentionally not rendered for native rules.
            </>
        ),
        renderEditor: ({ config, onStorageRefresh, refreshAfterNativeInteraction, renderLargeEditorContext }) => {
            const statementConfig = config as StatementRuleConfiguration;

            return (
                <NativeAppGlueEditorWithPopout
                    title={statementConfig.title ?? "AppGlue expression"}
                    executableType="StatementRuleConfiguration"
                    frameType="expression"
                    refreshAfterNativeInteraction={refreshAfterNativeInteraction}
                    renderLargeEditorContext={renderLargeEditorContext}
                    renderEditor={(instanceKey) => (
                        <ExpressionBuilder
                            key={`${statementConfig.__id}-${instanceKey}`}
                            expression={statementConfig.expression}
                            onUpdate={onStorageRefresh}
                        />
                    )}
                />
            );
        },
    },
    RuleTableConfiguration: {
        executableType: "RuleTableConfiguration",
        editorSurface: "native_rule_table_builder",
        title: "Native AppGlue Rule Table Builder",
        isWide: true,
        isConfig: (config) => config instanceof RuleTableConfiguration,
        description: (ruleDefinition) => (
            <>
                Editing renders AppGlue <code>RuleTableBuilder</code> for{" "}
                <code>{ruleDefinition.targetAppGlueObjectLabel}</code>. Use the builder save button or refresh preview
                before the drawer save button when checking intermediate counts.
            </>
        ),
        renderEditor: ({ config, onStorageRefresh, onCancel, refreshAfterNativeInteraction, renderLargeEditorContext }) => {
            const ruleTableConfig = config as RuleTableConfiguration;

            return (
                <>
                    <NativeAppGlueEditorWithPopout
                        title={ruleTableConfig.title ?? "AppGlue rule table"}
                        executableType="RuleTableConfiguration"
                        frameType="builder"
                        refreshAfterNativeInteraction={refreshAfterNativeInteraction}
                        renderLargeEditorContext={renderLargeEditorContext}
                        renderEditor={(instanceKey) => (
                            <RuleTableBuilder
                                key={`${ruleTableConfig._id}-${instanceKey}`}
                                ruleDefinition={ruleTableConfig}
                                ruleTableName={ruleTableConfig.title ?? "AppGlue rule table"}
                                onRuleSave={onStorageRefresh}
                                onRuleCancel={onCancel}
                            />
                        )}
                    />
                    <RuleActions>
                        <SecondaryButton
                            type="button"
                            onClick={onStorageRefresh}
                        >
                            Refresh validation preview
                        </SecondaryButton>
                    </RuleActions>
                </>
            );
        },
    },
    ValueTableConfiguration: {
        executableType: "ValueTableConfiguration",
        editorSurface: "native_value_table_builder",
        title: "Native AppGlue Value Table Builder",
        isWide: true,
        isConfig: (config) => config instanceof ValueTableConfiguration,
        description: (ruleDefinition) => (
            <>
                Editing renders AppGlue <code>ValueTableRuleBuilder</code> for{" "}
                <code>{ruleDefinition.targetAppGlueObjectLabel}</code>. The compact logic summary is generated from this
                value-table config.
            </>
        ),
        renderEditor: ({ config, onStorageRefresh, onCancel, refreshAfterNativeInteraction, renderLargeEditorContext }) => {
            const valueTableConfig = config as ValueTableConfiguration;

            return (
                <>
                    <NativeAppGlueEditorWithPopout
                        title={valueTableConfig.title ?? "AppGlue value table"}
                        executableType="ValueTableConfiguration"
                        frameType="builder"
                        refreshAfterNativeInteraction={refreshAfterNativeInteraction}
                        renderLargeEditorContext={renderLargeEditorContext}
                        renderEditor={(instanceKey) => (
                            <ValueTableRuleBuilder
                                key={`${valueTableConfig._id}-${instanceKey}`}
                                ruleDefinition={valueTableConfig}
                                valueTableName={valueTableConfig.title ?? "AppGlue value table"}
                                onRuleSave={onStorageRefresh}
                                onRuleCancel={onCancel}
                            />
                        )}
                    />
                    <RuleActions>
                        <SecondaryButton
                            type="button"
                            onClick={onStorageRefresh}
                        >
                            Refresh validation preview
                        </SecondaryButton>
                    </RuleActions>
                </>
            );
        },
    },
    TextMergeConfiguration: {
        executableType: "TextMergeConfiguration",
        editorSurface: "native_text_merge_builder",
        title: "Native AppGlue Text Merge Builder",
        isWide: true,
        isConfig: (config) => config instanceof TextMergeConfiguration,
        description: (ruleDefinition) => (
            <>
                Editing renders AppGlue <code>TextMergeBuilder</code> for{" "}
                <code>{ruleDefinition.targetAppGlueObjectLabel}</code>. Demo rules can opt into this host by registering
                a TextMergeConfiguration-backed adapter.
            </>
        ),
        renderEditor: ({ config, onStorageRefresh, onCancel, refreshAfterNativeInteraction, renderLargeEditorContext }) => {
            const textMergeConfig = config as TextMergeConfiguration;

            return (
                <NativeAppGlueEditorWithPopout
                    title="AppGlue text merge"
                    executableType="TextMergeConfiguration"
                    frameType="builder"
                    refreshAfterNativeInteraction={refreshAfterNativeInteraction}
                    renderLargeEditorContext={renderLargeEditorContext}
                    renderEditor={(instanceKey) => (
                        <TextMergeBuilder
                            key={`${textMergeConfig._id}-${instanceKey}`}
                            definition={textMergeConfig}
                            textMergeName="AppGlue text merge"
                            onSave={onStorageRefresh}
                            onCancel={onCancel}
                            onChange={onStorageRefresh}
                        />
                    )}
                />
            );
        },
    },
};

export const getRegisteredIntegratedRuleEditorHosts = (): IntegratedRuleEditorHostRegistration[] =>
    Object.values(INTEGRATED_RULE_EDITOR_HOSTS);

export const hasIntegratedRuleEditorHost = (executableType: string): boolean =>
    Object.prototype.hasOwnProperty.call(INTEGRATED_RULE_EDITOR_HOSTS, executableType);

export const getIntegratedRuleEditorHostRegistration = (
    config: AppGlueStorageBackedConfig | null,
): IntegratedRuleEditorHostRegistration | null => {
    if (!config) return null;
    return getRegisteredIntegratedRuleEditorHosts().find((host) => host.isConfig(config)) ?? null;
};

export const getIntegratedRuleEditorHostKey = (
    config: AppGlueStorageBackedConfig | null,
): IntegratedRuleEditorHostKey | null => getIntegratedRuleEditorHostRegistration(config)?.executableType ?? null;

export const isWideIntegratedRuleEditor = (config: AppGlueStorageBackedConfig | null): boolean => {
    return getIntegratedRuleEditorHostRegistration(config)?.isWide ?? false;
};

type IntegratedRuleEditorHostProps = {
    ruleDefinition: DemoRuleLogicDefinition;
    config: AppGlueStorageBackedConfig | null;
    onStorageRefresh: () => void;
    onCancel: () => void;
    renderLargeEditorContext?: () => React.ReactNode;
};

type NativeAppGlueEditorWithPopoutProps = {
    title: string;
    executableType: IntegratedRuleEditorHostKey;
    frameType: "expression" | "builder";
    refreshAfterNativeInteraction: () => void;
    renderLargeEditorContext?: () => React.ReactNode;
    renderEditor: (instanceKey: "inline" | "popout") => React.ReactNode;
};

const NativeAppGlueEditorWithPopout = (props: NativeAppGlueEditorWithPopoutProps) => {
    const [isPopoutOpen, setIsPopoutOpen] = React.useState(false);
    const frameProps = {
        "data-testid": INTEGRATED_RULE_EDITOR_FRAME_TEST_ID,
        "data-appglue-executable-type": props.executableType,
        onKeyUp: props.refreshAfterNativeInteraction,
        onMouseUp: props.refreshAfterNativeInteraction,
    };

    const inlineContent = isPopoutOpen ? (
        <PopoutInlinePlaceholder>Large editor is open.</PopoutInlinePlaceholder>
    ) : (
        props.renderEditor("inline")
    );

    return (
        <>
            <RuleActions>
                <SecondaryButton
                    type="button"
                    aria-label={`Open ${props.title} in a large editor`}
                    onClick={() => setIsPopoutOpen(true)}
                >
                    <ButtonIcon>
                        <ExpandIcon alt="" />
                    </ButtonIcon>
                    Open large editor
                </SecondaryButton>
            </RuleActions>
            {props.frameType === "expression" ? (
                <NativeExpressionFrame {...frameProps}>{inlineContent}</NativeExpressionFrame>
            ) : (
                <NativeRuleBuilderFrame {...frameProps}>{inlineContent}</NativeRuleBuilderFrame>
            )}
            <NativeBuilderDialog
                open={isPopoutOpen}
                onClose={() => setIsPopoutOpen(false)}
                fullScreen
                maxWidth={false}
            >
                <PopoutHeader>
                    <PopoutTitle>{props.title}</PopoutTitle>
                    <IconButton
                        aria-label="Close large editor"
                        onClick={() => setIsPopoutOpen(false)}
                    >
                        <CloseGrayIcon />
                    </IconButton>
                </PopoutHeader>
                <PopoutBody $hasContext={Boolean(props.renderLargeEditorContext)}>
                    <NativeRuleBuilderPopoutFrame
                        data-testid={INTEGRATED_RULE_EDITOR_POPOUT_TEST_ID}
                        data-appglue-executable-type={props.executableType}
                        onKeyUp={props.refreshAfterNativeInteraction}
                        onMouseUp={props.refreshAfterNativeInteraction}
                    >
                        {props.renderEditor("popout")}
                    </NativeRuleBuilderPopoutFrame>
                    {props.renderLargeEditorContext ? (
                        <PopoutContextPanel>{props.renderLargeEditorContext()}</PopoutContextPanel>
                    ) : null}
                </PopoutBody>
            </NativeBuilderDialog>
        </>
    );
};

export const IntegratedRuleEditorHost = (props: IntegratedRuleEditorHostProps) => {
    const host = getIntegratedRuleEditorHostRegistration(props.config);

    if (host && props.config) {
        const refreshAfterNativeInteraction = () => {
            globalThis.setTimeout(props.onStorageRefresh, 0);
        };

        return (
            <NativeEditorSurface
                data-testid={INTEGRATED_RULE_EDITOR_SURFACE_TEST_ID}
                data-appglue-executable-type={host.executableType}
                data-appglue-editor-surface={host.editorSurface}
            >
                <DrawerSectionTitle>{host.title}</DrawerSectionTitle>
                <OutcomeMeta>{host.description(props.ruleDefinition)}</OutcomeMeta>
                {host.renderEditor({
                    ...props,
                    config: props.config,
                    refreshAfterNativeInteraction,
                    renderLargeEditorContext: props.renderLargeEditorContext,
                })}
            </NativeEditorSurface>
        );
    }

    return (
        <NativeEditorSurface data-testid="electronics-adapter-backed-rule-summary">
            <DrawerSectionTitle>AppGlue Adapter-Backed Object</DrawerSectionTitle>
            <OutcomeMeta>
                Runtime object: <code>{props.ruleDefinition.targetAppGlueObjectLabel}</code>
                {props.config ? (
                    <>
                        {" "}
                        | No editor host found for <code>{props.ruleDefinition.targetAppGlueObjectLabel}</code>
                    </>
                ) : null}
            </OutcomeMeta>
            <OutcomeText>No executable editor host is available for this rule yet.</OutcomeText>
        </NativeEditorSurface>
    );
};

const DrawerSection = styled.section`
    display: grid;
    gap: 10px;
`;

const DrawerSectionTitle = styled.h3`
    margin: 0;
    color: #111827;
    font-size: 15px;
    line-height: 20px;
    font-weight: 800;
`;

const NativeEditorSurface = styled(DrawerSection)`
    border: 1px solid #111827;
    border-radius: 8px;
    background: #ffffff;
    padding: 14px;
`;

const NativeExpressionFrame = styled.div`
    min-height: 280px;
    overflow: auto;
`;

const NativeRuleBuilderFrame = styled.div`
    height: 620px;
    min-height: 0;
    overflow: auto;
    border: 1px solid #d9e0ea;
    border-radius: 8px;
    background: #f8fafc;
`;

const NativeRuleBuilderPopoutFrame = styled(NativeRuleBuilderFrame)`
    height: 100%;
    width: 100%;
    min-width: 0;
    border-radius: 0;
`;

const NativeBuilderDialog = styled(Dialog)`
    .MuiDialog-paper {
        display: grid;
        grid-template-rows: auto minmax(0, 1fr);
        background: #f8fafc;
    }
`;

const PopoutHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid #d9e0ea;
    background: #ffffff;
    padding: 14px 20px;
`;

const PopoutTitle = styled.h2`
    margin: 0;
    color: #111827;
    font-size: 18px;
    line-height: 24px;
    font-weight: 800;
`;

const PopoutBody = styled.div<{ $hasContext: boolean }>`
    display: grid;
    grid-template-columns: ${({ $hasContext }) => ($hasContext ? "minmax(0, 1fr) 390px" : "minmax(0, 1fr)")};
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    min-height: 0;
    overflow: hidden;
    padding: 16px;
`;

const PopoutContextPanel = styled.div`
    display: grid;
    align-content: start;
    gap: 12px;
    min-width: 0;
    overflow: auto;
`;

const PopoutInlinePlaceholder = styled.div`
    display: flex;
    min-height: 180px;
    align-items: center;
    justify-content: center;
    color: #667085;
    font-size: 13px;
    font-weight: 700;
`;

const RuleActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

const ButtonIcon = styled.span`
    display: inline-flex;
    width: 14px;
    height: 14px;
    margin-right: 7px;
    vertical-align: -2px;

    img {
        width: 14px;
        height: 14px;
    }
`;

const SecondaryButton = styled.button`
    border: 1px solid #cfd7e3;
    background: #f8fafc;
    color: #111827;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    padding: 9px 13px;
    cursor: pointer;

    &:hover {
        background: #eef2f7;
    }
`;

const OutcomeText = styled.div`
    margin-top: 9px;
    color: #344054;
    font-size: 13px;
    line-height: 19px;
`;

const OutcomeMeta = styled.div`
    margin-top: 7px;
    color: #667085;
    font-size: 12px;

    code {
        color: #111827;
        font-family: "Source Code Pro", monospace;
        font-size: 11px;
    }
`;
